import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const Activities = () => {
  const [activities, setActivities] = useState([]);
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchActivities();
  }, []);

  useEffect(() => {
    filterActivities();
  }, [selectedCategory, activities]);

  const fetchActivities = async () => {
    try {
      const response = await axios.get('/api/activities');
      setActivities(response.data);
      setFilteredActivities(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching activities:', error);
      setLoading(false);
    }
  };

  const filterActivities = () => {
    if (selectedCategory === 'all') {
      setFilteredActivities(activities);
    } else {
      const filtered = activities.filter(activity => activity.category === selectedCategory);
      setFilteredActivities(filtered);
    }
  };

  const categories = [
    { value: 'all', label: 'All Activities' },
    { value: 'Fitness Classes', label: 'Fitness Classes' },
    { value: 'Sports', label: 'Sports' },
    { value: 'Monthly Events', label: 'Monthly Events' }
  ];

  if (loading) {
    return <div style={styles.loading}>Loading activities...</div>;
  }

  return (
    <div style={styles.pageContainer}>
      {/* Hero Section */}
      <div style={styles.hero}>
        <div style={styles.breadcrumb}>
          <Link to="/" style={styles.breadcrumbLink}>Home</Link>
          <span style={styles.breadcrumbSeparator}>›</span>
          <span style={styles.breadcrumbCurrent}>Our Activities</span>
        </div>
        <h1 style={styles.heroTitle}>Our Activities</h1>
      </div>

      {/* Main Content */}
      <div style={styles.container}>
        {/* Category Filter */}
        <div style={styles.filtersContainer}>
          {categories.map(cat => (
            <button
              key={cat.value}
              onClick={() => setSelectedCategory(cat.value)}
              style={{
                ...styles.filterBtn,
                ...(selectedCategory === cat.value ? styles.filterBtnActive : {})
              }}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Activities Grid */}
        <div style={styles.grid}>
          {filteredActivities.length === 0 ? (
            <div style={styles.noResults}>
              <h3>No activities found</h3>
              <p>Try selecting a different category</p>
            </div>
          ) : (
            filteredActivities.map(activity => (
              <div key={activity._id} style={styles.card}>
                {/* Image */}
                <div style={styles.imageContainer}>
                  <img 
                    src={activity.image} 
                    alt={activity.name}
                    style={styles.image}
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23f0f0f0" width="400" height="300"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="30" dy="10.5" font-weight="bold" x="50%25" y="50%25" text-anchor="middle"%3E' + encodeURIComponent(activity.name) + '%3C/text%3E%3C/svg%3E';
                    }}
                  />
                </div>

                {/* Content */}
                <div style={styles.cardContent}>
                  {/* Category Badge */}
                  <span style={styles.categoryBadge}>{activity.category}</span>

                  {/* Title */}
                  <h3 style={styles.cardTitle}>{activity.name}</h3>

                  {/* Meta Info */}
                  <div style={styles.metaContainer}>
                    <div style={styles.metaItem}>
                      <span style={styles.metaIcon}></span>
                      <span style={styles.metaText}>{activity.duration}</span>
                    </div>
                    <div style={styles.metaItem}>
                      <span style={styles.metaIcon}></span>
                      <span style={styles.metaText}>{activity.day}</span>
                    </div>
                    <div style={styles.metaItem}>
                      <span style={styles.metaIcon}></span>
                      <span style={styles.metaText}>{activity.availableSpots} spots</span>
                    </div>
                  </div>



                  {/* Footer */}
                  <div style={styles.cardFooter}>
                    {activity.bookingOpen ? (
                      <Link 
                        to={`/activities/${activity._id}`}
                        style={styles.bookBtn}
                      >
                        Join Our Class
                        <span style={styles.btnIcon}>→</span>
                      </Link>
                    ) : (
                      <button style={styles.btnDisabled} disabled>
                        Booking Closed
                      </button>
                    )}
                    <div style={styles.price}>KES {activity.price}</div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const styles = {
  pageContainer: {
    background: '#FAFAFA',
    minHeight: '100vh'
  },
  hero: {
    background: 'linear-gradient(135deg, #ac7ba6 0%, #1a5632 100%)',
    padding: '4rem 2rem 3rem',
    textAlign: 'center',
    position: 'relative',
    backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M0 0h100v100H0z\' fill=\'%23ac7ba6\'/%3E%3Cpath d=\'M50 0L100 50L50 100L0 50Z\' fill=\'%231a5632\' opacity=\'.1\'/%3E%3C/svg%3E")',
    backgroundSize: '100px 100px'
  },
  breadcrumb: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '1.5rem',
    fontSize: '0.95rem'
  },
  breadcrumbLink: {
    color: 'rgba(255,255,255,0.8)',
    textDecoration: 'none',
    transition: 'color 0.3s'
  },
  breadcrumbSeparator: {
    color: 'rgba(255,255,255,0.6)'
  },
  breadcrumbCurrent: {
    color: 'white',
    fontWeight: 600
  },
  heroTitle: {
    fontSize: '3rem',
    color: 'white',
    margin: 0,
    fontFamily: 'Playfair Display, serif',
    fontWeight: 700
  },
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '3rem 2rem'
  },
  loading: {
    textAlign: 'center',
    padding: '4rem',
    fontSize: '1.2rem',
    color: '#5A5A5A'
  },
  filtersContainer: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'center',
    marginBottom: '3rem',
    flexWrap: 'wrap'
  },
  filterBtn: {
    padding: '0.75rem 1.75rem',
    fontSize: '0.95rem',
    border: '2px solid #E0E0E0',
    borderRadius: '12px',
    background: 'white',
    cursor: 'pointer',
    fontWeight: 600,
    color: '#5A5A5A',
    transition: 'all 0.3s',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
  },
  filterBtnActive: {
    background: 'linear-gradient(135deg, #E75A7C, #FFA8B9)',
    color: 'white',
    borderColor: '#E75A7C'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
    gap: '2rem'
  },
  noResults: {
    gridColumn: '1 / -1',
    textAlign: 'center',
    padding: '4rem',
    color: '#888'
  },
  card: {
    background: 'white',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    transition: 'transform 0.3s, box-shadow 0.3s',
    cursor: 'pointer'
  },
  imageContainer: {
    height: '240px',
    overflow: 'hidden',
    position: 'relative'
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.3s'
  },
  cardContent: {
    padding: '1.75rem'
  },
  categoryBadge: {
    display: 'inline-block',
    background: 'linear-gradient(135deg, #E75A7C, #FFA8B9)',
    color: 'white',
    padding: '0.4rem 1rem',
    borderRadius: '20px',
    fontSize: '0.8rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  cardTitle: {
    fontSize: '1.4rem',
    fontWeight: 700,
    color: '#2C2C2C',
    margin: '1rem 0',
    lineHeight: 1.3
  },
  metaContainer: {
    display: 'flex',
    gap: '1.25rem',
    marginBottom: '1rem',
    flexWrap: 'wrap'
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    fontSize: '0.9rem',
    color: '#5A5A5A'
  },
  metaIcon: {
    fontSize: '1rem'
  },
  metaText: {
    fontWeight: 500
  },
  
  reviewCount: {
    fontSize: '0.85rem',
    color: '#888',
    fontWeight: 500
  },
  cardFooter: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem'
  },
  bookBtn: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    padding: '0.875rem 1.5rem',
    background: 'linear-gradient(135deg, #7AB845, #5A9634)',
    color: 'white',
    border: 'none',
    borderRadius: '20px',
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'none',
    transition: 'all 0.3s',
    boxShadow: '0 4px 12px rgba(122, 184, 69, 0.3)'
  },
  btnIcon: {
    fontSize: '1.2rem',
    transition: 'transform 0.3s'
  },
  btnDisabled: {
    width: '100%',
    padding: '0.875rem 1.5rem',
    background: '#F5F5F5',
    color: '#999',
    border: '1px solid #E0E0E0',
    borderRadius: '6px',
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'not-allowed'
  },
  price: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#1a5632',
    textAlign: 'center'
  }
};

export default Activities;